import {
  SaveMilestone_Input,
  useGetMilestoneQuery,
  useSaveMilestoneMutation
} from 'generated/graphql';
import _ from 'lodash';
import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

export type IFormState = {
  description: string;
  durationInDays: string;
  endDate: string;
  id: string;
  projectId: string;
  startDate: string;
  statusId: string;
  title: string;
};

const initialState = {
  id: '',
  description: '',
  durationInDays: '',
  endDate: '',
  projectId: '',
  startDate: '',
  statusId: '',
  title: ''
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      description: {
        label: 'Description',
        type: 'text'
      },
      durationInDays: {
        label: 'Duration in days',
        type: 'number',
        required: true
      },
      endDate: {
        label: 'End date',
        type: 'date',
        required: true
      },
      // estimatedStartDate: {
      //   label: 'Estimated start date',
      //   type: 'date'
      // },
      id: {
        label: 'Id',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      startDate: {
        label: 'Start date',
        type: 'date',
        required: true
      },
      statusId: {
        label: 'Status id',
        type: 'text'
      },
      title: {
        label: 'Title',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.projectId === '' && projectId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (data.id !== '' && formState.id === '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveMilestoneForm() {
  const [
    fnMutate,
    tuple
  ] = useSaveMilestoneMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveMilestone_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends Omit<IFormState, 'projectId'> {}

export function useQuery(id: string) {
  const tuple = useGetMilestoneQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const milestone = tuple.data?.milestone;
      const data: IData = {
        id: milestone?.id ?? '',
        description: milestone?.description ?? '',
        durationInDays: _.toString(milestone?.durationInDays) ?? '',
        endDate: milestone?.endDate?.toFormat('yyyy-LL-dd') ?? milestone?.startDate.plus({
          days: milestone.durationInDays
        }).toFormat('yyyy-LL-dd') ?? '',
        startDate: milestone?.startDate
          ? milestone.startDate?.toFormat('yyyy-LL-dd')
          : milestone?.estimatedStartDate?.toFormat('yyyy-LL-dd') ?? '',
        statusId: milestone?.status.id ?? '',
        title: milestone?.title ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
